import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { EditorPlaceholder } from '@digilize/shared/definitions/src';
import { TranslocoService } from '@jsverse/transloco';

import { EMOJI_ICON_PATH } from './emoji-picker-lazy/emoji-icon';
import { loadEmojiPickerComponent } from './emoji-picker-lazy/emoji-picker-loader';

@Component({
  selector: 'lib-text-editor',
  templateUrl: './text-editor.component.html',
  styleUrls: ['./text-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TextEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('editorElement', { static: false }) editorElement;

  @Input() label: false | string = false;
  @Input() charMax = 1000;
  @Input() countOnly = false;
  @Input() translation = true;
  @Input() ckconfig = {
    toolbar: ['bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockquote'],
    link: {
      addTargetToExternalLinks: true,
    },
  } as { toolbar: string[]; link?: { addTargetToExternalLinks: boolean } };
  @Input() control: UntypedFormControl = new UntypedFormControl();
  /**
   * Work-in-progress state, e.g. while an AI generation is about to overwrite this field: locks
   * the editor and greys it out behind a centred spinner, so it is obvious that the field is
   * being written to rather than just refusing input.
   *
   * The lock goes through the ckeditor component's `disabled` input (which sets CKEditor's own
   * `isReadOnly`), deliberately NOT `control.disable()` - a disabled control is dropped from
   * `form.value`, which would silently strip the field from a save payload built while locked.
   */
  @Input() busy = false;
  @Input() placeholders: EditorPlaceholder[] = [];
  @Input() placeholdersNoBraces = false;
  /** Shows the emoji-picker button in the toolbar. On by default everywhere; set false to hide it for a usage. */
  @Input() emojiPicker = true;
  /**
   * Enables inline images. CKEditor calls this for every picked or dropped file and expects the
   * uploaded URL back; resolve with `{ default: url }` only - any other key is read as a srcset
   * width. The handler is an input because this lib cannot depend on the API services, and adding
   * `imageUpload` to `ckconfig.toolbar` is what puts the button in the toolbar.
   */
  @Input() imageUploadHandler?: (file: File) => Promise<{ default: string }>;

  public editor = ClassicEditor;
  charCounter;

  private emojiOverlayRef?: OverlayRef;
  private emojiButtonEl?: HTMLElement;
  private emojiUnlisten?: () => void;
  private emojiLoading = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private overlay: Overlay,
    private vcr: ViewContainerRef,
    private renderer: Renderer2,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    if (this.control.value) {
      this.validateDescription(this.htmlToString(this.control.value).length);
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.emojiUnlisten?.();
    this.emojiOverlayRef?.dispose();
  }

  onEditorChange($event) {
    this.charCounter = this.htmlToString($event.editor.getData()).length;
    this.validateDescription(this.charCounter);
  }

  /** Injects the emoji button as the last item of the CKEditor toolbar once the editor is rendered. */
  onEditorReady(editor: any): void {
    this.registerImageUploadAdapter(editor);

    if (!this.emojiPicker || this.emojiButtonEl) {
      return;
    }

    const toolbarEl = editor?.ui?.view?.toolbar?.element as HTMLElement | undefined;
    const itemsEl = (toolbarEl?.querySelector('.ck-toolbar__items') as HTMLElement | null) ?? toolbarEl;
    if (itemsEl) {
      this.createEmojiButton(itemsEl);
    }
  }

  addPlaceholder(placeholder) {
    const selection = this.editorElement.editorInstance.model.document.selection;
    const range = selection.getFirstRange();
    placeholder = placeholder.replace(/\{|\}/g, ''); /* from backend values already have {} */
    this.editorElement.editorInstance.model.change((writer) => {
      this.placeholdersNoBraces
        ? writer.insert(`${placeholder}`, range.start)
        : writer.insert(`{${placeholder}}`, range.start);
    });
  }

  async toggleEmojiPicker() {
    if (this.emojiOverlayRef) {
      this.closeEmojiPicker();
      return;
    }
    if (this.emojiLoading || !this.emojiButtonEl) {
      return;
    }

    this.emojiLoading = true;
    this.setEmojiButtonLoading(true);

    try {
      /* Shared module-scoped loader → fetched at most once, even with several editors on the page. */
      const { EmojiPickerLazyComponent } = await loadEmojiPickerComponent();

      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.emojiButtonEl)
        .withPositions([
          { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
        ]);

      this.emojiOverlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
      });

      this.emojiOverlayRef.backdropClick().subscribe(() => this.closeEmojiPicker());
      this.emojiOverlayRef.keydownEvents().subscribe((event) => {
        if (event.key === 'Escape') {
          this.closeEmojiPicker();
        }
      });

      const componentRef = this.emojiOverlayRef.attach(new ComponentPortal(EmojiPickerLazyComponent, this.vcr));
      componentRef.instance.emojiSelect.subscribe((native: string) => this.insertEmoji(native));
    } finally {
      this.emojiLoading = false;
      this.setEmojiButtonLoading(false);
    }
  }

  closeEmojiPicker() {
    this.emojiOverlayRef?.dispose();
    this.emojiOverlayRef = undefined;
    this.editorElement?.editorInstance?.editing.view.focus();
  }

  insertEmoji(native: string) {
    this.insertText(native);
  }

  /** Inserts text at the editor's caret. Public so a control outside the toolbar can drive it. */
  insertText(text: string) {
    const editorInstance = this.editorElement?.editorInstance;
    if (!editorInstance || !text) {
      return;
    }

    /* insertContent inserts at the current selection and advances the cursor past the text, so
       picking several emoji in a row (the picker stays open) keeps them in order. */
    const model = editorInstance.model;
    model.change((writer) => {
      model.insertContent(writer.createText(text));
    });
  }

  /** The bundled build ships FileRepository but no adapter, so an upload fails until one is set. */
  private registerImageUploadAdapter(editor: any): void {
    const fileRepository = this.imageUploadHandler ? editor?.plugins?.get('FileRepository') : null;

    if (!fileRepository) {
      return;
    }

    fileRepository.createUploadAdapter = (loader) => ({
      upload: () => loader.file.then((file: File) => this.imageUploadHandler(file)),
      abort: () => undefined,
    });
  }

  private createEmojiButton(itemsEl: HTMLElement): void {
    const label = this.transloco.translate('ck_editor.emoji_button');
    const button = this.renderer.createElement('button') as HTMLButtonElement;

    this.renderer.setAttribute(button, 'type', 'button');
    this.renderer.setAttribute(button, 'title', label);
    this.renderer.setAttribute(button, 'aria-label', label);
    ['ck', 'ck-button', 'ck-off', 'emoji-trigger'].forEach((cssClass) => this.renderer.addClass(button, cssClass));
    button.innerHTML =
      `<svg class="ck ck-icon ck-button__icon" width="20" height="20" viewBox="0 0 24 24" ` +
      `xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="${EMOJI_ICON_PATH}" /></svg>`;

    this.emojiUnlisten = this.renderer.listen(button, 'click', () => {
      this.toggleEmojiPicker();
    });
    this.renderer.appendChild(itemsEl, button);
    this.emojiButtonEl = button;
  }

  private setEmojiButtonLoading(loading: boolean): void {
    if (!this.emojiButtonEl) {
      return;
    }
    loading
      ? this.renderer.addClass(this.emojiButtonEl, 'is-loading')
      : this.renderer.removeClass(this.emojiButtonEl, 'is-loading');
  }

  setCounter() {
    this.charCounter = this.htmlToString(this.control.value).length;
  }

  validateDescription(charCounter: number) {
    const invalid = !this.countOnly ? charCounter > this.charMax : false;
    this.control.setErrors({ charMax: invalid });
    if (!invalid) {
      this.control.updateValueAndValidity();
    }
  }

  htmlToString(html: string): string {
    let text = html.replace(/<[^>]*>/g, '');
    text = text.replace('&nbsp', '');
    return text;
  }
}
